// ============================================================
// FichePiece — l'identité de la pièce, À CÔTÉ du plan.
// ------------------------------------------------------------
// ── Pourquoi à côté et pas dessous ──
//
// Le deviseur doit décrire comment il usinerait la pièce. Pour ça il
// doit VOIR la pièce pendant qu'il écrit. Le dessin et le champ de
// consigne étaient l'un sous l'autre, séparés par mille pixels : on
// décrivait de mémoire, ou on ne décrivait pas.
//
// Sur cette colonne on trouve donc, dans cet ordre : de quoi être sûr
// qu'on parle de la bonne pièce — client, projet, référence,
// désignation, quantité, nature — puis la question.
//
// ── Ce que ce bloc N'EST PAS ──
//
// Ce n'est pas un second endroit où saisir. Le client et le projet
// appartiennent au devis : ils s'affichent en LECTURE, avec leur
// libellé, parce qu'ils servent à identifier — pas à être modifiés ici.
// Ce qui se saisit est ce qui appartient à la pièce et qu'on corrige en
// regardant le plan : la référence, la désignation, la quantité et la
// nature. Un même champ modifiable à deux endroits, c'est la question
// « lequel gagne ? » qu'on passe la session à supprimer.
// ============================================================

import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import { TextField, Select } from '../../ui/TextField.jsx';
import NumberField from '../../ui/NumberField.jsx';

/**
 * D'où vient la quantité, quand elle vient du mail. On COMPARE le fait
 * lu à la valeur retenue plutôt que de tenir une provenance à jour : si
 * le deviseur l'a changée, l'écran le dit sans qu'il ait rien à cocher.
 */
function origineQuantite(piece) {
  if (!piece.qty_mail) return undefined;
  const extrait = piece.qty_mail_extrait ? ` (« ${piece.qty_mail_extrait} »)` : '';
  return Number(piece.qty) === Number(piece.qty_mail)
    ? `Lue dans le mail${extrait}.`
    : `Le mail annonçait ${piece.qty_mail}${extrait}.`;
}

/** Une valeur qu'on lit, jamais qu'on tape ici. */
function Info({ label, valeur }) {
  return (
    <div className="fiche__info">
      <span className="fiche__label">{label}</span>
      <span className="fiche__valeur">{valeur || '—'}</span>
    </div>
  );
}

export function FichePiece({
  devis, client, piece, disabled, onChange,
}) {
  return (
    <Card>
      <CardHead title={piece.name || piece.ref || 'Pièce'} />
      <CardBody className="stack">
        {/* Le contexte, en lecture : il vient du devis et se modifie
            là-haut. Il est ici pour qu'on sache de quelle affaire on
            parle sans remonter. */}
        <div className="fiche__infos">
          <Info label="Client" valeur={client?.company} />
          <Info label="Projet" valeur={devis?.project} />
          <Info label="Commande client" valeur={devis?.client_order_ref} />
        </div>

        {/* La consigne n'est plus ici : elle est montée AU-DESSUS du plan,
            sur toute la largeur (Charles, 15 septembre 2026). Voir
            `DevisPage.jsx`. */}
        <div className="grid-2">
          <TextField
            label="Référence"
            value={piece.ref ?? ''}
            disabled={disabled}
            onChange={(e) => onChange({ ref: e.target.value })}
          />
          <TextField
            label="Désignation"
            value={piece.name ?? ''}
            disabled={disabled}
            onChange={(e) => onChange({ name: e.target.value })}
          />
          <NumberField
            label="Quantité"
            value={piece.qty}
            min={1}
            hint={origineQuantite(piece)}
            disabled={disabled}
            onChange={(v) => onChange({ qty: v })}
          />
          <Select
            label="Nature"
            value={piece.is_purchase ? 'purchase' : 'machined'}
            disabled={disabled}
            options={[
              { value: 'machined', label: 'Pièce usinée' },
              { value: 'purchase', label: 'Composant du commerce' },
            ]}
            onChange={(e) => onChange({ is_purchase: e.target.value === 'purchase' })}
          />
        </div>

      </CardBody>
    </Card>
  );
}

export default FichePiece;
